import { ButtonType, ButtonWidget, Chip, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType, DialogWidget, ListItem, ListItemFilterFn, LocallyFilteringList, TabContent, TabListAlignment, TabsWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { callAddUpdateConfigViaApiService, GeocodingConfig } from '~src/geocoding/requests/GeocodingConfigRequests';

import { CreateCustomDialogFn, ReactModalService } from '../../../commonInfra/dialogs/ReactModalService';
import { EstimateTab } from './EstimateTab';
import { GeocodeTab } from './GeocodeTab';

export interface GeocodingConfigDialogProps {
  datasourceName: string;
  datasourceLuid: string;
  geocodingConfig?: GeocodingConfig;
  columns: ListItem[];
  colNameToCaptionMap: Record<string, string>;
  close: () => void;
  updateRecord: () => void;
}

/**
 * Options used for ReactModalService
 */
export function LauchGeocodingConfigDialog(props: Omit<GeocodingConfigDialogProps, 'close'>): CreateCustomDialogFn {
  return (closeAndDismiss) => {
    const dialogProps = ReactModalService.getDefaultDialogProps({
      type: DialogType.NormalWithTitleBar,
      title: Messages.geocodingDialogTitle({DS_NAME: props.datasourceName}),
      dialogWidth: 700,
      shouldNotCloseOnGlassClick: true,
      positioningBehavior: DialogPositioningBehavior.Draggable,
      showCloseButton: true,
      disableAutoFocus: true,
      testId: 'geocoding-config-dialog'
    }, closeAndDismiss);

    return (
      <DialogWidget {...dialogProps}>
        <GeocodingConfigDialog
          datasourceName={props.datasourceName}
          datasourceLuid={props.datasourceLuid}
          geocodingConfig={props.geocodingConfig}
          columns={props.columns}
          colNameToCaptionMap={props.colNameToCaptionMap}
          close={closeAndDismiss.close}
          updateRecord={props.updateRecord}
        />
      </DialogWidget>
    );
  };
}

interface ConfigDialogState {
  selectedColumnName?: string;
  isGeocodeTabActive: boolean;
  columns: ListItem[];
  tokenLimit: number;
}

export class GeocodingConfigDialog extends React.Component<GeocodingConfigDialogProps, ConfigDialogState> {
  public constructor(props: GeocodingConfigDialogProps) {
    super(props);
    this.state = {
      selectedColumnName: this.props.geocodingConfig?.targetColName,
      isGeocodeTabActive: true,
      columns: props.columns,
      tokenLimit: this.props.geocodingConfig?.tokenLimit ?? 100
    };
  }

  private itemFilterFn: ListItemFilterFn = (item, filterValue) => item.tooltip.toLowerCase().indexOf(filterValue.toLowerCase()) >= 0;

  private buildColumnList(): React.ReactNode {
    return (
      <div className='geocodingConfigDialogColList'>
        <div>{Messages.geocodingConfigDialogListLabel()}</div>
        <LocallyFilteringList
          items={this.state.columns}
          placeholder={Messages.geocodingConfigDialogSearch()}
          onSelect={(selected) => this.setState({ selectedColumnName: selected })}
          selectedValue={this.state.selectedColumnName}
          itemFilterFn={this.itemFilterFn}
        />
      </div>
    );
  }

  private buildFieldConfigArea(): React.ReactNode {
    const { selectedColumnName } = this.state;
    if (!selectedColumnName) {
      return <div className='geocodingFieldConfigArea'>{Messages.geocodingConfigDialogNoSelectionText()}</div>;
    }

    return (
      <div className='geocodingFieldConfigArea'>
        <div>
          <div className='geocodingChipLabel'>{Messages.geocodingConfigDialogChipLabel()}</div>
          {this.renderChip(selectedColumnName)}
        </div>
        <div className='geocodingInfoTextBlock'>
          <div className='geocodingBoldText'>{Messages.geocodingConfigDialogInfoTitle()}</div>
          <ul>
            <li>{Messages.geocodingConfigDialogInfoTip1()}</li>
            <li>{Messages.geocodingConfigDialogInfoTip2()}</li>
            <li>{Messages.geocodingConfigDialogInfoTip3()}</li>
            <li>{Messages.geocodingConfigDialogInfoTip4()}</li>
            <li>{Messages.geocodingConfigDialogInfoTip5()}</li>
          </ul>
        </div>
        <ButtonWidget className='geocodingDsEditorBtn'>{Messages.geocodingConfigDialogDSEditorButton()}</ButtonWidget>
      </div>
    );
  }

  private renderChip(colName: string): JSX.Element {
    const colCaption = this.props.colNameToCaptionMap[colName];
    return (
      <Chip className='geocodingColumnChip' key={colName} tooltip={colCaption} value={colName} onRemove={() => this.setState({ selectedColumnName: undefined })}>
        {colCaption}
      </Chip>
    );
  }

  private handleTabChanged = (index: number) => {
    if (index === 0) {
      this.setState({isGeocodeTabActive: false});
    } else {
      this.setState({isGeocodeTabActive: true});
    }
  };

  private buildTabs(): React.ReactNode {
    const { selectedColumnName, tokenLimit } = this.state;
    return (
      <TabsWidget
        className='geocodingConfigTabs'
        ariaLabel='Tabs'
        initialActiveIndex={1}
        tabListAlignment={TabListAlignment.Left}
        onActiveIndexChanged={this.handleTabChanged}
        tabs={[
          {
            tabContent: { label: Messages.geocodingConfigEstimateTabLabel() } as TabContent,
            panelContent: <EstimateTab targetColName={selectedColumnName} />,
          },
          {
            tabContent: { label: Messages.geocodingConfigGeocodeTabLabel() } as TabContent,
            panelContent: <GeocodeTab targetColName={selectedColumnName} tokenLimit={tokenLimit} handleTokenLimitChange={(val) => this.setState({ tokenLimit: val })} />,
          },
        ]}
        testId="config-tabs"
      />
    );
  }

  public addGeocodingConfigs(datasourceLuid: string): void {
    callAddUpdateConfigViaApiService(
      {
        datasourceLuid: datasourceLuid,
        configName: this.props.colNameToCaptionMap[this.state.selectedColumnName],
        colName: this.state.selectedColumnName,
        tokenLimit: this.state.tokenLimit,
        configId: this.props.geocodingConfig?.id
      })
    .then(
      () => {
        this.props.updateRecord();
        return Promise.resolve();
      },
      () => {
        return Promise.reject();
      }
    );
  }

  private handleConfirmAction = () => {
    this.addGeocodingConfigs(this.props.datasourceLuid);
    this.props.close();
  };

  private renderFooterButtons(): JSX.Element {
    return (
      <DialogFooterButtonsWidget
        buttons={[
          {
            children: Messages.geocodingDialogCancelButton(),
            handleClick: this.props.close,
          }, {
            buttonType: ButtonType.Primary,
            children: this.state.isGeocodeTabActive
              ? Messages.geocodingConfigGeocodeButton()
              : Messages.geocodingConfigEstimateButton(),
            disabled: !this.state.selectedColumnName,
            handleClick: this.handleConfirmAction,
          }
        ]}
      />
    );
  }

  public render(): React.ReactNode {
    return (
      <div className='geocodingConfgDialogContainer'>
        <div className='geocodingConfigDialogMainContent'>
          {this.buildColumnList()}
          <div className='geocodingConfigDialogContent'>
            {this.buildFieldConfigArea()}
            {this.buildTabs()}
          </div>
        </div>
        {this.renderFooterButtons()}
      </div>
    );
  }
}
